import {
	FloatingFocusManager,
	FloatingPortal,
	autoUpdate,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
	useTransitionStyles,
} from "@floating-ui/react";
import { cva } from "class-variance-authority";
import * as React from "react";
import { useId, useState } from "react";

import { DropDownLinkOption, type DropDownMenuOption, DropDownOption } from "./DropDownMenuOption";

import { cn } from "../../../lib/utils";

const openButtonVariants = cva("h-8 cursor-pointer rounded-1 bg-transparent p-2 text-paragraph-3", {
	variants: {
		bordered: {
			true: "border border-neutral-3 hover:border hover:border-neutral-5",
			false: "border-none",
		},
		open: {
			true: "border border-neutral-5",
		},
	},
});

export type DropDownMenuProps = {
	/**
	 * options to display
	 */
	options: DropDownMenuOption[];
	/**
	 * active option key
	 */
	activeKey?: string;
	/**
	 * disabled menu
	 */
	disabled?: boolean;
	/**
	 * additional class wrapper
	 */
	className?: string;
	/**
	 * additional class wrapper for the menu
	 */
	menuClassName?: string;
	/**
	 * additional style wrapper
	 */
	style?: React.CSSProperties;
	/**
	 * onSelect Function
	 * @param _key
	 * @returns
	 */
	onSelect?: (_key: string) => void;

	/**
	 * close on select
	 * @default true
	 */
	closeOnSelect?: boolean;

	/**
	 * testid maps onto wrapper data-testid
	 */
	testid?: string;

	/**
	 * optional min width for the drop down menu options
	 */
	minWidth?: number | null;

	/**
	 * button Contents
	 */
	children?: React.ReactNode;

	/**
	 * placement of the dropdown menu
	 */
	placement?:
		| "bottom-end"
		| "bottom-start"
		| "top-end"
		| "top-start"
		| "right-start"
		| "right-end"
		| "left-start"
		| "left-end"
		| "bottom"
		| "top"
		| "right"
		| "left";

	/**
	 * if the dropdown menu button should have a border
	 */
	bordered?: boolean;
};

export const DropDownMenu = ({
	options,
	className,
	menuClassName,
	style,
	activeKey,
	onSelect = () => {},
	disabled,
	testid,
	closeOnSelect = true,
	minWidth = null,
	children,
	placement,
	bordered = false,
}: DropDownMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const floatProps = {
		open: isOpen,
		placement,
		onOpenChange: setIsOpen,
		whileElementsMounted: autoUpdate,
		middleware: [offset(8), flip({ fallbackAxisSideDirection: "end" }), shift()],
	};

	const { refs, floatingStyles, context } = useFloating(floatProps);

	// https://floating-ui.com/docs/useclick#useclick
	// click outside of the dropdown menu to close it
	const click = useClick(context);

	// https://floating-ui.com/docs/usedismiss#usedismiss
	// dismiss the dropdown menu when the escape key is pressed
	const dismiss = useDismiss(context);

	// https://floating-ui.com/docs/userole#userole
	// Adds base screen reader props to the reference and floating elements for a given role.
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	// https://floating-ui.com/docs/usetransition
	const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
		duration: { open: 1000, close: 250 },
		close: {
			maxHeight: 0,
		},
		open: {
			maxHeight: 1000,
		},
		initial: {
			maxHeight: 0,
		},
	});

	const headingId = useId();

	let dropDownWidth: number | null = null;
	if (minWidth) {
		dropDownWidth = minWidth;
	} else {
		dropDownWidth = refs.reference.current ? refs.reference.current.getBoundingClientRect().width : 100;
	}

	const handleSelect = (key: string) => {
		onSelect(key);
		if (closeOnSelect) {
			setIsOpen(false);
		}
	};

	return (
		<div className={className} style={style} data-testid={testid}>
			<button
				data-testid="dropdown-button"
				disabled={disabled}
				className={cn(openButtonVariants({ open: isOpen, bordered }), className)}
				ref={refs.setReference}
				{...getReferenceProps()}
			>
				{children}
			</button>
			{(isOpen || isMounted) && (
				<FloatingPortal>
					<FloatingFocusManager context={context} modal={false}>
						<div
							className={cn(
								"h-fit-content overflow-hidden rounded-[4px] border border-solid border-neutral-3 bg-neutral-0",
								menuClassName
							)}
							ref={refs.setFloating}
							style={{
								...transitionStyles,
								...floatingStyles,
								zIndex: 1000,
								minWidth: dropDownWidth ?? 160,
							}}
							aria-labelledby={headingId}
							{...getFloatingProps()}
							data-testid="floating-menu"
						>
							{options.map((option) => {
								return (
									<div key={option.key}>
										{option?.href ? (
											<DropDownLinkOption option={option} isActive={option.key === activeKey} />
										) : (
											<DropDownOption
												option={option}
												isActive={option.key === activeKey}
												onClick={() => {
													option.onClick?.();
													handleSelect(option.key);
												}}
											/>
										)}
									</div>
								);
							})}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</div>
	);
};
